//TODO: We need to revisit this player implementation, this is intended to work for version 1.0.25
// of the GKVideoPlayer, some technical debits should be fixed in the lib and this implementation updated soon

import { GKMuxPlayer, GKVideoPlayerProvider } from '@nicheinc/gk-video-player';
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from 'react';
import styled, { css } from 'styled-components';

import { DisplayIcon, SpinningLoader, Text } from '@nicheinc/campus-ds';
import { replaceMergeToBraceFormat } from '@global/design-system/atoms';
import { StoriesType } from '@app/types/modules/stories';
import { VideoScreensEnum } from './constants';
import {
	VideoStateActionTypeNameEnum,
	VideoStateEnum,
	getVideoStateInitialValue,
	handleVideoStateReducer,
} from './reducer';
import { VideoStateActionPayloadType } from './types';
import {
	checkIsVideoPlayable,
	getPlayerTheme,
	getVideoRecorder,
	handleFormatCallToActions,
} from './utils';
import { CTATypeEnum } from '@app/constants/modules/ctas';
import { CTA } from '@app/redux/cta/types';
import { getStorytellerName } from '@app/utils/modules/storytellers';
import { checkIsReels } from '@app/utils/modules/campaigns';
import { WorkspaceType } from '@app/redux/workspace/types';

const PlayerContainer = styled.div<{
	thumbnail: string;
	hideTracker?: boolean | null;
	hidePoster?: boolean | null;
}>`
	display: flex;
	width: 100%;
	height: 100%;
	position: relative;

	${({ hideTracker }) =>
		hideTracker &&
		css`
			.mux-video::part(time range) {
				display: none !important;
			}
		`}

	${({ hidePoster }) =>
		hidePoster &&
		css`
			.mux-video::part(poster) {
				display: none !important;
			}
		`}
`;

type PropsType = {
	story: Partial<StoriesType>;
	hideCaptions?: boolean | null;
	hideCaptionButton?: boolean | null;
	hidePreviewTitle?: boolean | null;
	hideEndTitle?: boolean | null;
	hideCTA?: boolean | null;
	hideHeader?: boolean | null;
	hideTracker?: boolean | null;
	thumbnailSeconds?: number | null;
	isEditingThumbnail?: boolean | null;
	disableEndScreen?: boolean | null;
	hidePoster?: boolean | null;
	loading?: boolean;
	workspaceInfo?: WorkspaceType;
	showLoader?: boolean;
};

const GKMuxPlayerWeb = forwardRef(function GKMuxPlayerWeb(
	{
		story,
		hideCaptions,
		hideCaptionButton,
		hidePreviewTitle,
		hideEndTitle,
		hideCTA,
		hideTracker,
		thumbnailSeconds,
		hideHeader = false,
		isEditingThumbnail = false,
		disableEndScreen,
		hidePoster,
		loading,
		showLoader = true,
		workspaceInfo,
	}: PropsType,
	ref: any,
) {
	const assertedStory = useMemo(() => {
		return {
			...story,
			workspace: workspaceInfo,
		} as StoriesType;
	}, [story, workspaceInfo]);

	const isAutoplay = assertedStory?.campaign?.settings?.autoplay; //TODO: check workspace

	const [isLoading, setIsLoading] = useState(false);
	const [isShowingOverlay, setIsShowingOverlay] = useState(true);
	const [isStaticPreview, _setIsStaticPreview] = useState(false);
	const [screen, setScreen] = useState(VideoScreensEnum.PAUSED);
	const [videoState, dispatchVideoState] = useReducer(
		handleVideoStateReducer,
		getVideoStateInitialValue({
			isMuted: !!isAutoplay,
		}),
	);

	const videoReferenceInternal = useRef<any | null>(null);

	const videoReference = ref || videoReferenceInternal;

	const video = useMemo(() => {
		return assertedStory?.video;
	}, [assertedStory?.video]);

	const isShowingCaptionWarning =
		assertedStory?.campaign?.settings?.captionWarning; //TODO: check workspace

	const isReels = checkIsReels(assertedStory?.campaign?.personalization || '');

	const template = assertedStory?.campaign?.template; //TODO: check

	const isEditMode = false;

	const isDemoMode = false;

	const isPreviewMode = false;

	const isVideoPlayable = checkIsVideoPlayable(assertedStory);

	const headerText = isReels
		? assertedStory?.campaign?.reelsTitle
		: template?.headerText;

	const endHeaderText = template?.endHeaderText;

	const contactFirstName = getVideoRecorder(assertedStory)?.firstName;

	const extendedTheme = getPlayerTheme(assertedStory);

	const videoProps = {
		currentVideo: video as any,
		videosPanelItems: [],
		videoReference,
		isActiveVideo: true,
		activeVideoIndex: 0,
		canNext: false,
		canPrev: false,
		videosCount: 1,
	};

	const handleScreenChange = useCallback((screen: VideoScreensEnum) => {
		setScreen(screen);
	}, []);

	const onVideoPlay = useCallback(() => {
		handleScreenChange(VideoScreensEnum.PLAY);
	}, [handleScreenChange]);

	const onVideoReplay = useCallback(() => {
		handleScreenChange(VideoScreensEnum.PLAY);
	}, [handleScreenChange]);

	const onVideoPaused = useCallback(
		(isPausedManually?: boolean) => {
			if (isPausedManually) {
				handleScreenChange(VideoScreensEnum.PAUSED);
			}
		},
		[handleScreenChange],
	);

	const onVideoEnded = useCallback(() => {
		if (disableEndScreen) {
			videoReference?.current?.pause();
			handleScreenChange(VideoScreensEnum.PAUSED);
			videoReference.current.currentTime = 0;

			return;
		}

		handleScreenChange(VideoScreensEnum.ENDED);
	}, [handleScreenChange]);

	const handleReplayAction = () => {
		videoReference.current.currentTime = 0;

		videoReference?.current?.play();
		onVideoReplay();
	};

	const handleVideoState = (payload: VideoStateActionPayloadType) => {
		dispatchVideoState({
			type: VideoStateActionTypeNameEnum.editState,
			payload,
		});
	};

	const handleMergeTagReplace = (text: string) => {
		return replaceMergeToBraceFormat(text, workspaceInfo);
	};

	const previewTitle = useMemo(() => {
		if (headerText) return handleMergeTagReplace(headerText);

		if (isEditMode) return `[Recorder First Name] has a message for you`;

		if (contactFirstName) return `${contactFirstName} has a message for you`;

		return 'Just a moment...';
	}, [headerText, isEditMode, contactFirstName]);

	const endTitle = useMemo(() => {
		if (!contactFirstName && !endHeaderText) return 'Thanks for watching!';
		if (endHeaderText) return handleMergeTagReplace(endHeaderText);
		if (isEditMode) return 'Thanks for watching!';

		return `Thanks for watching, ${contactFirstName}!`;
	}, [contactFirstName, endHeaderText, isEditMode]);

	const isAutoplayEnabled = useMemo(() => {
		return isDemoMode || isStaticPreview ? false : isAutoplay;
	}, [isDemoMode, isAutoplay, isStaticPreview]);

	const screenProps = {
		isReels: false,
		reelsTitle: assertedStory?.campaign?.reelsTitle,
		isMobileScreen: false,
		isEditMode,
		isStaticPreview: isStaticPreview,
		isAutoplay: isAutoplayEnabled,
		isDemo: isDemoMode,
		isPreview: isPreviewMode,
		isShowingOverlay,
		setIsShowingOverlay,
		activeScreen: screen,
		setActiveScreen: handleScreenChange,
		isVideosPanelOpen: false,
		setIsVideosPanelOpen: () => {},
		disablePreview: true,
		disableOnScreenElements: !!isEditingThumbnail,
	};

	const headerProps = useMemo(() => {
		const storyteller = getVideoRecorder(assertedStory);
		const hideRecordersName =
			assertedStory?.campaign?.settings?.hideRecordersName;

		const getTitle = () => {
			if (isReels) return assertedStory?.title || extendedTheme?.name;

			return storyteller
				? getStorytellerName(storyteller)
				: '[Recorder First Name]';
		};

		const getSubTitle = () => {
			if (isReels)
				return hideRecordersName ? '' : getStorytellerName(storyteller);

			return extendedTheme?.name;
		};

		const fields = hideHeader
			? {
					title: '',
					subtitle: '',
					logo: '',
					onLogoClick: () => {},
				}
			: {
					title: getTitle(),
					subtitle: getSubTitle(),
					logo: extendedTheme?.logo,
					onLogoClick: () => {},
					hideCaptionButton: !!hideCaptionButton,
				};

		return fields;
	}, [template, assertedStory, extendedTheme, hideCaptionButton]);

	const previewProps = useMemo(() => {
		return {
			bgImage: isStaticPreview
				? template?.thumbnailImage || ''
				: video?.muxVideoPreviewGIF
					? video?.muxVideoPreviewGIF
					: video?.thumbnail || template?.thumbnailImage,
			buttonText: isLoading
				? 'Captions are auto-generated'
				: template?.playButtonText || 'Watch video',
			isLoading: isLoading,
			title: hidePreviewTitle ? '' : previewTitle,
		};
	}, [
		isLoading,
		template,
		video,
		previewTitle,
		isStaticPreview,
		hidePreviewTitle,
	]);

	const endProps = useMemo(() => {
		return {
			title: hideEndTitle ? '' : endTitle,
			buttonText: 'Watch again',
			bgImage: video?.muxThumbnail || template?.thumbnailImage,
		};
	}, [endTitle, template, video, hideEndTitle]);

	const formattedCtas = useMemo(() => {
		// The goal of this is to eliminate the process of passing down the "replacemergeTags" method to the player
		// Parsed and processed values should be passed to the player.
		const formmattedActions = handleFormatCallToActions({
			actions: assertedStory?.campaign?.callToActions?.filter(
				(cta: CTA) => cta?.type !== CTATypeEnum.SUGGESTED_REPLY,
			),
			handleReplaceMergeTags: handleMergeTagReplace,
		});

		return formmattedActions;
	}, [assertedStory?.callToActions]);

	const ctaProps = useMemo(() => {
		return hideCTA
			? {
					actions: [],
					onCallToActionClick: () => {},
				}
			: {
					actions: formattedCtas,
					onCallToActionClick: () => {},
				};
	}, [formattedCtas, hideCTA]);

	const videoEvents = useMemo(() => {
		return {
			onPlay: onVideoPlay,
			onReplay: onVideoReplay,
			onPause: onVideoPaused,
			onEnd: onVideoEnded,
		};
	}, [onVideoPlay, onVideoReplay, onVideoPaused, onVideoEnded]);

	//handle hide captions through video state passed to player
	useEffect(() => {
		handleVideoState({
			stateName: VideoStateEnum.captionOn,
			stateValue: !hideCaptions,
		});

		if (video?.hideCaptions) {
			handleVideoState({
				stateName: VideoStateEnum.captionOn,
				stateValue: false,
			});
		}
	}, [hideCaptions, video?.hideCaptions]);

	//set video time when thumbnail seconds changes, reset state when moving out of thumbnail edit
	useEffect(() => {
		if (videoReference.current && isEditingThumbnail) {
			videoReference.current.currentTime = thumbnailSeconds;
			videoReference.current.pause();
			setIsShowingOverlay(false);
			handleScreenChange(VideoScreensEnum.PLAY);

			handleVideoState({
				stateName: VideoStateEnum.isPlaying,
				stateValue: false,
			});
		}

		if (
			!isEditingThumbnail &&
			videoReference.current &&
			!videoState?.isPlaying
		) {
			videoReference.current.currentTime = 0;
			handleScreenChange(VideoScreensEnum.PAUSED);
			setIsShowingOverlay(true);
		}
	}, [thumbnailSeconds, videoReference.current, isEditingThumbnail]);

	// loading timeout
	useEffect(() => {
		const loadingTimeout = setTimeout(
			() => {
				setIsLoading(false);
			},
			isShowingCaptionWarning ? 2000 : 50,
		);

		return () => {
			clearInterval(loadingTimeout);
		};
	}, []);

	return (
		<>
			{loading && showLoader && (
				<div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
					<SpinningLoader loading={loading} />
				</div>
			)}
			{isVideoPlayable && !loading && (
				<GKVideoPlayerProvider extendedTheme={extendedTheme}>
					<PlayerContainer
						thumbnail={endProps?.bgImage || previewProps?.bgImage || ''}
						hideTracker={hideTracker}
						hidePoster={hidePoster}
					>
						<GKMuxPlayer
							captionHeight={80}
							headerProps={headerProps}
							previewProps={previewProps}
							endProps={endProps}
							videoActions={{
								handleNext: () => {},
								handlePrev: () => {},
								setCurrentVideo: () => {},
								handleReplay: handleReplayAction,
							}}
							ctaProps={ctaProps as any}
							videoProps={videoProps}
							screenProps={screenProps}
							stepperProps={{}}
							replyProps={{
								onSubmit: () => {},
							}}
							videoState={videoState}
							handleVideoState={handleVideoState}
							videoEvents={videoEvents}
							disableGlobalKeyEvents={true}
						/>
					</PlayerContainer>
				</GKVideoPlayerProvider>
			)}

			{!loading && !isVideoPlayable && (
				<div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
					<DisplayIcon iconName="videoCamera" variant="system" />
					<Text color="system.text.medium" size="small" lineHeight="large">
						{!video
							? 'No video yet'
							: video?.status === 'Processing'
								? 'Video is being processed'
								: 'Video is not available'}
					</Text>
				</div>
			)}
		</>
	);
});

export default GKMuxPlayerWeb;
export type { PropsType as GKMuxPlayerWebProps };
